import { flattenToAppURL } from '@plone/volto/helpers';

import { HOME_MENU_ITEM } from './constants';

/*
------------------------------------------------------------
Nó da navegação (@navigation)
------------------------------------------------------------
O formato é o que o reducer customizado de navegação entrega
(customizations/volto/reducers/navigation): além do título, cada nó traz a
URL já achatada para a app e o tipo de conteúdo.

`href` só aparece quando aponta para fora do próprio nó — o destino de um
`Link`. Para todo o resto, a URL é a de `url`.

`icon`, `teaser` e `highlight` vêm do behavior `menu_meta` do backend, que os
publica no @navigation por `customize_entry`. São opcionais: conteúdo sem o
campo preenchido simplesmente não traz a chave.

`headerLink` (campo `nav_link_target`) só vem nos itens de nível 1: é o destino
do cabeçalho (ícone + título) do mega menu. Sem ele, o cabeçalho não é link.
------------------------------------------------------------
*/
export interface MenuItem {
  title: string;
  url?: string;
  href?: string;
  type?: string;
  icon?: string;
  teaser?: string;
  highlight?: boolean;
  headerLink?: string;
  items?: MenuItem[];
}

/* Chave de um nó: a URL, ou o título quando o nó não tem URL. */
export const menuItemKey = (item: MenuItem): string => item.url || item.title;

/*
A chave de um nó vem com barras (`/o-mpf`), que valem como id em HTML5 mas
quebram como seletor — daí a higienização, já que esses ids são alvo de
`aria-controls`.
*/
export const sanitizeId = (rawId: string): string =>
  rawId.replace(/[^a-zA-Z0-9_-]/g, '-');

/* Id do painel do mega menu. */
export const megaMenuPanelId = (rawId: string): string =>
  `mega-${sanitizeId(rawId)}`;

/*
------------------------------------------------------------
Item da home
------------------------------------------------------------
O item que aponta para a raiz do site aparece só como ícone de casa: o rótulo
continua no HTML (leitores de tela e busca) e some da tela. A detecção é pela
URL, não pelo título, para não depender de como a home foi nomeada.

Ficam aqui, e não no `Menu`, porque a barra azul do desktop e o painel mobile
precisam das duas — e o painel não deve importar lógica da barra.
------------------------------------------------------------
*/
export const isHomeItem = (item: MenuItem): boolean => {
  const rawUrl = item.url ?? item.href ?? '';
  return rawUrl === '' || flattenToAppURL(rawUrl) === '/';
};

/*
A home abre o menu. Se a navegação já trouxer a raiz (depende de como o site
está configurado), usa-se a entrada dela; senão, entra a fixa — nunca as duas.
*/
export const withHomeItem = (navItems: MenuItem[]): MenuItem[] =>
  navItems.some(isHomeItem) ? navItems : [HOME_MENU_ITEM, ...navItems];
